import {
  Banner,
  BlockStack,
  Button,
  Card,
  InlineGrid,
  Modal,
  Spinner,
  Text,
} from '@shopify/polaris';
import { type ReactNode, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { api } from '../lib/api.js';
import { getAppBridge } from '../lib/app-bridge.js';
import type { BillingStatus } from '../lib/types.js';
import { AppPage } from '../components/Layout.js';

/**
 * Design #09 — Pricing page. Three tiers with feature lists, tags,
 * "Most popular" badge, and FAQ.
 */
export function Plans(): JSX.Element {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [billing, setBilling] = useState<BillingStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [chargeBusy, setChargeBusy] = useState<string | null>(null);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelBusy, setCancelBusy] = useState(false);

  const refreshBilling = useCallback(async (signal?: AbortSignal) => {
    const b = await api.get<BillingStatus>('/api/billing/status', signal);
    setBilling(b);
  }, []);

  useEffect(() => {
    const ac = new AbortController();
    (async () => {
      try {
        await refreshBilling(ac.signal);
      } catch (e) {
        if ((e as Error).name === 'AbortError') return;
        setError((e as Error).message);
      } finally {
        setLoading(false);
      }
    })();
    return () => ac.abort();
  }, [refreshBilling]);

  const startCharge = useCallback(async (plan: 'full' | 'monitoring') => {
    setChargeBusy(plan);
    setError(null);
    try {
      const res = await api.post<{ confirmationUrl: string }>('/api/billing/charge', { plan });
      const top = window.top ?? window;
      top.location.href = res.confirmationUrl;
    } catch (e) {
      setError((e as Error).message);
      setChargeBusy(null);
    }
  }, []);

  const confirmCancel = useCallback(async () => {
    setCancelBusy(true);
    setError(null);
    try {
      await api.post('/api/billing/cancel');
      await refreshBilling();
      setCancelOpen(false);
      const app = await getAppBridge().catch(() => null);
      app?.toast.show(t('plans.cancelSuccess'));
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setCancelBusy(false);
    }
  }, [refreshBilling, t]);

  if (loading) {
    return (
      <AppPage title={t('plans.title')} backAction={{ content: t('plans.back'), onAction: () => navigate('/') }}>
        <div style={{ display: 'flex', justifyContent: 'center', padding: 48 }}>
          <Spinner accessibilityLabel="Loading" size="large" />
        </div>
      </AppPage>
    );
  }

  if (!billing) {
    return (
      <AppPage title={t('plans.title')} backAction={{ content: t('plans.back'), onAction: () => navigate('/') }}>
        {error && <Banner tone="critical">{error}</Banner>}
      </AppPage>
    );
  }

  const isFree = billing.plan === 'free';
  const isFull = billing.plan === 'full';
  const isMonitoring = billing.plan === 'monitoring';

  return (
    <AppPage
      title={t('plans.title')}
      subtitle={t('plans.subtitle')}
      backAction={{ content: t('plans.back'), onAction: () => navigate('/') }}
    >
      <BlockStack gap="500">
        {error && <Banner tone="critical" onDismiss={() => setError(null)}>{error}</Banner>}

        {/* 3-tier grid */}
        <InlineGrid columns={{ xs: 1, md: 3 }} gap="400">
          {/* Free */}
          <TierCard
            name={t('plans.tierName.free')}
            price="$0"
            period={t('plans.freeForever')}
            description={t('plans.freeDescription')}
            features={[
              { text: t('plans.featureScanLimit', { limit: 25 }), included: true },
              { text: t('plans.featureAiDetection'), included: true },
              { text: t('plans.featureImageChecks'), included: true },
              { text: t('plans.featureRegulationMatching'), included: true },
              { text: t('plans.featureFixSuggestions'), included: true },
              { text: t('plans.featureDisclosureInserter'), included: true },
              { text: t('plans.featurePdfExport'), included: true },
              { text: t('plans.featureNoBulkFix'), included: false },
              { text: t('plans.featureNoMonitoring'), included: false },
            ]}
            buttonLabel={isFree ? t('plans.currentLabel') : undefined}
            buttonDisabled={isFree}
          />

          {/* Full */}
          <TierCard
            name={t('plans.tierName.full')}
            price={`$${billing.pricing.full.price}`}
            period={t('plans.oneTime')}
            description={t('plans.fullDescription')}
            features={[
              { text: t('plans.featureScanLimit', { limit: 500 }), included: true, tag: 'one-time', tagTone: 'success' },
              { text: 'Everything in Free', included: true },
              { text: t('plans.featureBulkFix'), included: true },
              { text: t('plans.featureValidationRescan'), included: true },
              { text: t('plans.featureNoOngoing'), included: false },
              { text: t('plans.featureNoAlerts'), included: false },
            ]}
            buttonLabel={
              isFull
                ? t('plans.currentLabel')
                : isMonitoring
                  ? t('plans.cancelMonitoringFirst')
                  : t('plans.upgradeTo', { price: billing.pricing.full.price })
            }
            buttonDisabled={isFull || isMonitoring}
            buttonLoading={chargeBusy === 'full'}
            onButtonClick={isFull || isMonitoring ? undefined : () => startCharge('full')}
            footer={
              isMonitoring ? (
                <div style={{ fontSize: 12, color: 'var(--p-color-text-subdued)', marginTop: 8, textAlign: 'center' }}>
                  {t('plans.cancelMonitoringHint')}
                </div>
              ) : null
            }
          />

          {/* Monitoring */}
          <TierCard
            name={t('plans.tierName.monitoring')}
            price={`$${billing.pricing.monitoring.price}`}
            priceSuffix={t('plans.perMonth')}
            period={t('plans.cancelAnytime')}
            description={t('plans.monitoringDescription')}
            featured
            features={[
              { text: t('plans.featureUnlimitedScans'), included: true, tag: 'resets monthly', tagTone: 'success' },
              { text: 'Everything in Full report', included: true },
              { text: t('plans.featureWeeklyRescan'), included: true },
              { text: t('plans.featureNewProductScan'), included: true },
              { text: t('plans.featureEmailAlerts'), included: true },
              { text: t('plans.featureRegulationUpdates'), included: true },
              { text: t('plans.featureBadge'), included: true },
              { text: t('plans.featureUnlimitedRescans'), included: true },
            ]}
            buttonLabel={isMonitoring ? t('plans.currentLabel') : t('plans.startMonitoring', { price: billing.pricing.monitoring.price })}
            buttonDisabled={isMonitoring}
            buttonLoading={chargeBusy === 'monitoring'}
            buttonPrimary
            onButtonClick={isMonitoring ? undefined : () => startCharge('monitoring')}
            footer={
              isMonitoring ? (
                <div style={{ marginTop: 8, textAlign: 'center' }}>
                  <Button
                    variant="plain"
                    tone="critical"
                    onClick={() => setCancelOpen(true)}
                  >
                    {t('plans.cancelMonitoring')}
                  </Button>
                </div>
              ) : null
            }
          />
        </InlineGrid>

        <Modal
          open={cancelOpen}
          onClose={() => (cancelBusy ? undefined : setCancelOpen(false))}
          title={t('plans.cancelConfirmTitle')}
          primaryAction={{
            content: t('plans.cancelConfirmAction'),
            destructive: true,
            loading: cancelBusy,
            onAction: () => {
              void confirmCancel();
            },
          }}
          secondaryActions={[
            {
              content: t('plans.cancelConfirmKeep'),
              disabled: cancelBusy,
              onAction: () => setCancelOpen(false),
            },
          ]}
        >
          <Modal.Section>
            <Text as="p">{t('plans.cancelConfirmBody')}</Text>
          </Modal.Section>
        </Modal>

        {/* FAQ */}
        <Card>
          <BlockStack gap="300">
            <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 6 }}>{t('plans.faqTitle')}</div>
            {[1, 2, 3, 4, 5].map((n) => (
              <div key={n} style={{ padding: '12px 0', borderBottom: n < 5 ? '1px solid #F3F4F6' : undefined }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: '#303030', marginBottom: 4 }}>
                  {t(`plans.faq${n}q`)}
                </div>
                <div style={{ fontSize: 13, color: '#6D7175', lineHeight: 1.6 }}>
                  {t(`plans.faq${n}a`)}
                </div>
              </div>
            ))}
          </BlockStack>
        </Card>
      </BlockStack>
    </AppPage>
  );
}

interface TierFeature {
  text: string;
  included: boolean;
  tag?: string;
  tagTone?: 'success' | 'info';
}

function TierCard({
  name,
  price,
  priceSuffix,
  period,
  description,
  featured,
  features,
  buttonLabel,
  buttonDisabled,
  buttonLoading,
  buttonPrimary,
  onButtonClick,
  footer,
}: {
  name: string;
  price: string;
  priceSuffix?: string;
  period: string;
  description: string;
  featured?: boolean;
  features: TierFeature[];
  buttonLabel?: string;
  buttonDisabled?: boolean;
  buttonLoading?: boolean;
  buttonPrimary?: boolean;
  onButtonClick?: () => void;
  footer?: ReactNode;
}): JSX.Element {
  return (
    <div style={{
      background: '#fff',
      border: featured ? '2px solid #005BD3' : '1px solid #E3E3E3',
      borderRadius: 12,
      padding: '24px 20px',
      display: 'flex',
      flexDirection: 'column',
      position: 'relative',
    }}>
      {featured && (
        <div style={{
          position: 'absolute', top: -10, left: '50%', transform: 'translateX(-50%)',
          background: '#005BD3', color: '#fff', fontSize: 11, fontWeight: 600,
          padding: '2px 14px', borderRadius: 10, whiteSpace: 'nowrap',
        }}>
          Most popular
        </div>
      )}
      <div style={{ fontSize: 13, color: 'var(--p-color-text-subdued)', fontWeight: 600, marginBottom: 6 }}>{name}</div>
      <div style={{ fontSize: 32, fontWeight: 700, marginBottom: 2 }}>
        {price}
        {priceSuffix && <span style={{ fontSize: 16, fontWeight: 400, color: 'var(--p-color-text-subdued)' }}>{priceSuffix}</span>}
      </div>
      <div style={{ fontSize: 12, color: 'var(--p-color-text-subdued)', marginBottom: 4 }}>{period}</div>
      <div style={{ fontSize: 13, color: 'var(--p-color-text-subdued)', lineHeight: 1.5, marginBottom: 16, minHeight: 40 }}>{description}</div>
      <div style={{ height: 1, background: '#F3F4F6', marginBottom: 16 }} />

      <div style={{ flex: 1, marginBottom: 20 }}>
        {features.map((f, i) => (
          <div key={i} style={{ display: 'flex', gap: 8, padding: '5px 0', fontSize: 13, lineHeight: 1.4, alignItems: 'flex-start' }}>
            <span style={{ color: f.included ? '#008060' : 'var(--p-color-text-subdued)', flexShrink: 0, fontSize: 14 }}>
              {f.included ? '✓' : '✕'}
            </span>
            <span style={{ color: f.included ? undefined : 'var(--p-color-text-subdued)' }}>
              {f.text}
              {f.tag && (
                <span style={{
                  background: f.tagTone === 'info' ? '#DBEAFE' : '#D1FAE5',
                  color: f.tagTone === 'info' ? '#1E40AF' : '#065F46',
                  fontSize: 10, fontWeight: 600, padding: '1px 6px', borderRadius: 4, marginLeft: 4,
                }}>
                  {f.tag}
                </span>
              )}
            </span>
          </div>
        ))}
      </div>

      {buttonLabel && (
        <Button
          variant={buttonPrimary ? 'primary' : undefined}
          disabled={buttonDisabled}
          loading={buttonLoading}
          onClick={onButtonClick}
          fullWidth
        >
          {buttonLabel}
        </Button>
      )}
      {footer}
    </div>
  );
}
